import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  User,
  FileText,
  Calendar,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  X,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Search // <--- Added Search icon for the input
} from 'lucide-react';
import { format } from 'date-fns';
import { submitRequestByHR, getRequestsByType, approveRequest, rejectRequest, deleteRequest } from '../services/requestAPIs';
import { useTheme } from '@mui/material';
import RichTextEditor from './RichTextEditor';

const EmployeeRequests = ({ employees, selectedDoc, setSelectedDoc, onDocSelect, documents, setActiveTab, setIsEditMode }) => {
  const location = useLocation();
  const theme = useTheme();
  const [requests, setRequests] = useState([]);
  const [formData, setFormData] = useState({
    employeeId: '',
    type: 'Document Request',
    description: '',
    file: null,
    priority: 'Medium',
  });
  const [errors, setErrors] = useState({});
  const [showForm, setShowForm] = useState(false);

  // New State for Viewing Details
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [sortBy, setSortBy] = useState('createdDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [darkMode, setDarkMode] = useState(false);
  const [showAuditInfo, setShowAuditInfo] = useState(false);
  const requestsPerPage = 10;

  useEffect(() => {
    if (location.state?.employeeFilter) {
      setFilterEmployee(location.state.employeeFilter);
      setFilterStatus(location.state?.status || 'pending');
    }
    if (location.state?.requestType) {
      setFilterType(location.state.requestType);
    }
    if (location.state?.status) {
      setFilterStatus(location.state.status);
    }
  }, [location.state]);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- NEW STATES FOR SEARCHABLE SELECT ---
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sort employees alphabetically
  const sortedEmployees = [...employees].sort((a, b) => {
    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Filter employees based on search
  const filteredEmployees = sortedEmployees.filter(emp => {
    const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  // Handle Employee Selection
  const handleEmployeeSelect = (emp) => {
    setFormData({ ...formData, employeeId: emp.employeeId });
    setSearchTerm(`${emp.firstName} ${emp.lastName}`);
    setShowDropdown(false);
    setErrors({ ...errors, employeeId: null }); // Clear error if exists
  };
  // ----------------------------------------

  useEffect(() => {
    const savedDraft = localStorage.getItem('requestDraft');
    if (savedDraft) {
      const parsed = JSON.parse(savedDraft);
      setFormData(parsed);
      // Try to pre-fill the search term if ID exists
      if (parsed.employeeId) {
        const found = employees.find(e => e.employeeId === parsed.employeeId);
        if (found) setSearchTerm(`${found.firstName} ${found.lastName}`);
      }
    }
  }, [employees]); // Added employees dependency to ensure list is loaded

  useEffect(() => {
    localStorage.setItem('requestDraft', JSON.stringify(formData));
  }, [formData]);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getRequestsByType(filterType || 'all');
      const transformedRequests = (Array.isArray(data) ? data : data.data || []).map(req => ({
        ...req,
        employeeName: req.employee ? `${req.employee.firstName} ${req.employee.lastName}` : 'N/A',
        createdBy: req.createdBy || 'SYSTEM',
        createdDate: req.createdDate ? new Date(req.createdDate).toISOString() : null,
        lastModifiedBy: req.lastModifiedBy || req.createdBy || 'SYSTEM',
        lastModifiedDate: req.lastModifiedDate ? new Date(req.lastModifiedDate).toISOString() : req.createdDate,
      }));
      setRequests(transformedRequests);
      console.log('Fetched Requests:', transformedRequests);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setError('Failed to fetch requests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.employeeId.trim()) newErrors.employeeId = 'Employee selection is required';
    if (!stripHtml(formData.description).trim()) newErrors.description = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        setLoading(true);
        const requestData = {
          employee: { employeeId: formData.employeeId },
          type: formData.type,
          description: formData.description,
          file: formData.file ? formData.file.name : null,
          priority: formData.priority,
        };
        const newRequest = await submitRequestByHR(requestData);
        const transformedRequest = {
          ...newRequest,
          employeeName: newRequest.employee ? `${newRequest.employee.firstName} ${newRequest.employee.lastName}` : 'N/A',
          createdBy: newRequest.createdBy || 'SYSTEM',
          createdDate: newRequest.createdDate ? new Date(newRequest.createdDate).toISOString() : null,
          lastModifiedBy: newRequest.lastModifiedBy || newRequest.createdBy || 'SYSTEM',
          lastModifiedDate: newRequest.lastModifiedDate ? new Date(newRequest.lastModifiedDate).toISOString() : newRequest.createdDate,
        };
        setRequests([transformedRequest, ...requests]);

        // Reset Form
        setFormData({
          employeeId: '',
          type: 'Document Request',
          description: '',
          file: null,
          priority: 'Medium',
        });
        setSearchTerm(''); // Reset Search
        setShowForm(false);
        localStorage.removeItem('requestDraft');
        setSuccess('Request submitted successfully! 🎉');
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error('Submission error:', error);
        setError('Failed to submit request. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleApprove = async (requestId) => {
    if (!window.confirm('Are you sure you want to approve this request?')) {
      return;
    }

    try {
      setLoading(true);
      const updatedRequest = await approveRequest(requestId);
      const transformedRequest = {
        ...updatedRequest,
        employeeName: updatedRequest.employee ? `${updatedRequest.employee.firstName} ${updatedRequest.employee.lastName}` : 'N/A',
        createdBy: updatedRequest.createdBy || 'SYSTEM',
        createdDate: updatedRequest.createdDate ? new Date(updatedRequest.createdDate).toISOString() : null,
        lastModifiedBy: updatedRequest.lastModifiedBy || updatedRequest.createdBy || 'SYSTEM',
        lastModifiedDate: updatedRequest.lastModifiedDate ? new Date(updatedRequest.lastModifiedDate).toISOString() : updatedRequest.createdDate,
      };

      setRequests(requests.map(req =>
        req.requestId === requestId ? transformedRequest : req
      ));

      // Update modal view if currently open
      if (selectedRequest && selectedRequest.requestId === requestId) {
        setSelectedRequest(transformedRequest);
      }

      setSuccess('Request approved successfully! ✅');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error approving request:', error);
      const errorMessage = error.message || 'Failed to approve request';

      // --- MODIFIED LOGIC: REMOVED REDIRECT CONDITION ---
      if (errorMessage.includes('Only pending requests')) {
        setError('This request has already been processed and cannot be approved.');
      } else {
        // Now just displays the error, no matter what it is
        setError(errorMessage);
      }
      // --------------------------------------------------

      setTimeout(() => setError(null), 5000);
      fetchRequests();
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (requestId) => {
    const reason = window.prompt('Please provide a reason for rejection:');
    if (!reason || !reason.trim()) {
      return;
    }

    try {
      setLoading(true);
      const updatedRequest = await rejectRequest(requestId, { reason: reason.trim() });
      const transformedRequest = {
        ...updatedRequest,
        employeeName: updatedRequest.employee ? `${updatedRequest.employee.firstName} ${updatedRequest.employee.lastName}` : 'N/A',
        createdBy: updatedRequest.createdBy || 'SYSTEM',
        createdDate: updatedRequest.createdDate ? new Date(updatedRequest.createdDate).toISOString() : null,
        lastModifiedBy: updatedRequest.lastModifiedBy || updatedRequest.createdBy || 'SYSTEM',
        lastModifiedDate: updatedRequest.lastModifiedDate ? new Date(updatedRequest.lastModifiedDate).toISOString() : updatedRequest.createdDate,
      };

      setRequests(requests.map(req =>
        req.requestId === requestId ? transformedRequest : req
      ));

      // Update modal view if currently open
      if (selectedRequest && selectedRequest.requestId === requestId) {
        setSelectedRequest(transformedRequest);
      }

      setSuccess('Request rejected successfully! ❌');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error rejecting request:', error);
      const errorMessage = error.message || 'Failed to reject request';
      if (errorMessage.includes('Only pending requests')) {
        setError('This request has already been processed and cannot be rejected.');
      } else {
        setError(errorMessage);
      }
      setTimeout(() => setError(null), 5000);
      fetchRequests();
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortedRequests = [...requests].sort((a, b) => {
    if (!sortBy) return 0;
    if (sortBy === 'createdDate' || sortBy === 'lastModifiedDate') {
      const dateA = new Date(a[sortBy] || a.createdDate);
      const dateB = new Date(b[sortBy] || b.createdDate);
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    }
    return sortOrder === 'asc'
      ? (a[sortBy] || '').toString().localeCompare((b[sortBy] || '').toString())
      : (b[sortBy] || '').toString().localeCompare((a[sortBy] || '').toString());
  });

  const filteredRequests = sortedRequests.filter(req => {
    const typeMatch = !filterType || (req.type && req.type.toLowerCase().includes(filterType.toLowerCase()));
    const statusMatch = !filterStatus || (req.status && req.status.toLowerCase() === filterStatus.toLowerCase());
    const employeeMatch = !filterEmployee || (
      (req.employeeName && req.employeeName.toLowerCase().includes(filterEmployee.toLowerCase())) ||
      (req.employee?.employeeId && req.employee.employeeId.toLowerCase().includes(filterEmployee.toLowerCase()))
    );
    return typeMatch && statusMatch && employeeMatch;
  });

  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(indexOfFirstRequest, indexOfLastRequest);
  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);

  const formatAuditDate = (dateString) => {
    if (!dateString || isNaN(new Date(dateString).getTime())) return 'N/A';
    return format(new Date(dateString), 'MMM d, yyyy, h:mm a');
  };

  const formatDate = (dateString) => {
    if (!dateString || isNaN(new Date(dateString).getTime())) return 'N/A';
    return format(new Date(dateString), 'MMM d, yyyy, h:mm a');
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return <CheckCircle size={16} className="status-icon approved" />;
      case 'rejected': return <XCircle size={16} className="status-icon rejected" />;
      case 'cancelled': return <X size={16} className="status-icon cancelled" />;
      default: return <Clock size={16} className="status-icon pending" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return '#ff4757';
      case 'medium': return '#ffa502';
      case 'low': return '#26de81';
      default: return '#747d8c';
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;

    try {
      setLoading(true);
      await deleteRequest(requestId);
      // Remove from list
      setRequests(requests.filter(r => r.requestId !== requestId));
      // Close modal if open
      if (selectedRequest && selectedRequest.requestId === requestId) {
        setSelectedRequest(null);
      }
      setSuccess('Request deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error deleting request:', error);
      setError('Failed to delete request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`employee-requests ${theme?.palette?.mode === 'dark' ? 'dark' : ''}`}>
      {error && (
        <div className="doc-notification doc-error">
          <AlertCircle className="doc-notification-icon doc-error-icon" />
          <span className="doc-notification-text">{error}</span>
          <button onClick={() => setError(null)} className="doc-notification-close">
            <X className="doc-close-icon" />
          </button>
        </div>
      )}

      {success && (
        <div className="doc-notification doc-success">
          <CheckCircle className="doc-notification-icon doc-success-icon" />
          <span className="doc-notification-text">{success}</span>
          <button onClick={() => setSuccess(null)} className="doc-notification-close">
            <X className="doc-close-icon" />
          </button>
        </div>
      )}

      <div className="request-content">
        <div className="doc-action-bar">
          <div className="controls">
            <button
              onClick={() => {
                setShowForm(!showForm);
                setSearchTerm(''); // Reset search when opening
              }}
              aria-label="Toggle request form"
              className="btn btn-primary"
              disabled={loading}
              style={{ fontFamily: 'inherit', fontSize: '14px' }}
            >
              {showForm ? 'Close Form ❌' : 'New Request ➕'}
            </button>
            <div className="search-box">
              <input
                type="text"
                placeholder="Filter by Employee... 🔍"
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
                className="filter-input"
                aria-label="Filter by employee"
                style={{ fontFamily: 'inherit', fontSize: '14px' }}
              />
            </div>
            <div className="search-box">
              <input
                type="text"
                placeholder="Filter by Request Type... 🔍"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="filter-input"
                aria-label="Filter by request type"
                style={{ fontFamily: 'inherit', fontSize: '14px' }}
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
              aria-label="Filter by status"
              style={{ fontFamily: 'inherit', fontSize: '14px' }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <div className="results-count">
              {filteredRequests.length} of {requests.length} requests
            </div>
            <button
              onClick={() => setShowAuditInfo(!showAuditInfo)}
              className="btn btn-secondary"
              aria-label={showAuditInfo ? 'Hide audit information' : 'Show audit information'}
              style={{ fontFamily: 'inherit', fontSize: '14px' }}
            >
              {showAuditInfo ? 'Hide Audit Info' : 'Show Audit Info'}
            </button>
          </div>
        </div>

        <div className="table-container">
          <table className="requests-table" aria-label="Employee requests table">
            <thead>
              <tr>
                <th onClick={() => handleSort('employeeName')} className="sortable">
                  <div className="header-content">
                    <User size={16} style={{ marginLeft: 0 }} />
                    Employee Name
                    {sortBy === 'employeeName' && (sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                  </div>
                </th>
                <th onClick={() => handleSort('type')} className="sortable">
                  <div className="header-content">
                    <FileText size={16} />
                    Type
                    {sortBy === 'type' && (sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                  </div>
                </th>
                <th onClick={() => handleSort('createdDate')} className="sortable">
                  <div className="header-content">
                    <Calendar size={16} />
                    Created Date
                    {sortBy === 'createdDate' && (sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                  </div>
                </th>
                <th onClick={() => handleSort('description')} className="sortable">
                  <div className="header-content">
                    Description
                    {sortBy === 'description' && (sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                  </div>
                </th>
                <th onClick={() => handleSort('priority')} className="sortable">
                  <div className="header-content">
                    Priority
                    {sortBy === 'priority' && (sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                  </div>
                </th>
                <th onClick={() => handleSort('status')} className="sortable">
                  <div className="header-content">
                    Status
                    {sortBy === 'status' && (sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                  </div>
                </th>
                {showAuditInfo && (
                  <>
                    <th onClick={() => handleSort('createdBy')} className="sortable">
                      <div className="header-content">
                        Created By
                        {sortBy === 'createdBy' && (sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                      </div>
                    </th>
                    <th onClick={() => handleSort('createdDate')} className="sortable">
                      <div className="header-content">
                        Created Date
                        {sortBy === 'createdDate' && (sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                      </div>
                    </th>
                    <th onClick={() => handleSort('lastModifiedBy')} className="sortable">
                      <div className="header-content">
                        Last Modified By
                        {sortBy === 'lastModifiedBy' && (sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                      </div>
                    </th>
                    <th onClick={() => handleSort('lastModifiedDate')} className="sortable">
                      <div className="header-content">
                        Last Modified Date
                        {sortBy === 'lastModifiedDate' && (sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                      </div>
                    </th>
                  </>
                )}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRequests.length === 0 ? (
                <tr>
                  <td colSpan={showAuditInfo ? 11 : 7} className="empty-state">
                    <div className="empty-content">
                      <FileText size={48} />
                      <h3>No requests found</h3>
                      <p>
                        {requests.length === 0
                          ? "No requests have been submitted yet."
                          : "Try adjusting your search or filter criteria"
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentRequests.map((req) => (
                  <tr key={req.requestId} className="request-row">
                    <td>
                      <div className="employee-name">
                        <strong>{req.employeeName || 'N/A'}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="request-type">{req.type}</span>
                    </td>
                    <td className="date-cell">
                      {formatDate(req.createdDate)}
                    </td>
                    <td>
                      <div 
                        className="description-cell"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        dangerouslySetInnerHTML={{ __html: req.description || '' }}
                      />
                    </td>
                    <td>
                      <span className={`priority-badge ${req.priority ? req.priority.toLowerCase() : 'medium'}`}
                        style={{ backgroundColor: getPriorityColor(req.priority || 'Medium') }}>
                        {req.priority || 'Unspecified'}
                      </span>
                    </td>
                    <td>
                      <div className="status-cell">
                        <span className={`status-badge ${req.status.toLowerCase()}`}>
                          {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                        </span>
                      </div>
                    </td>
                    {showAuditInfo && (
                      <>
                        <td>{req.createdBy}</td>
                        <td>{formatAuditDate(req.createdDate)}</td>
                        <td>{req.lastModifiedBy}</td>
                        <td>{formatAuditDate(req.lastModifiedDate)}</td>
                      </>
                    )}
                    <td>
                      <div className="action-buttons">
                        {/* View Button */}
                        <button
                          onClick={() => setSelectedRequest(req)}
                          className="btn btn-primary btn-sm"
                          title="View Full Details"
                          style={{
                            backgroundColor: '#3b82f6',
                            border: 'none',
                            padding: '4px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginRight: '4px',
                            cursor: 'pointer',
                            color: 'white',
                            borderRadius: '4px'
                          }}
                        >
                          <Eye size={12} /> View
                        </button>

                        {req.status && req.status.toLowerCase() === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(req.requestId)}
                              className="btn btn-success btn-sm"
                              disabled={loading}
                              title="Approve request"
                              style={{
                                fontFamily: 'inherit',
                                fontSize: '12px',
                                padding: '4px 8px',
                                marginRight: '4px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              ✅ Approve
                            </button>
                            <button
                              onClick={() => handleReject(req.requestId)}
                              className="btn btn-danger btn-sm"
                              disabled={loading}
                              title="Reject request"
                              style={{
                                fontFamily: 'inherit',
                                fontSize: '12px',
                                padding: '4px 8px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              ❌ Reject
                            </button>
                          </>
                        )}

                        <button
                          onClick={() => handleDelete(req.requestId)}
                          className="btn btn-secondary"
                          disabled={loading}
                          style={{ backgroundColor: '#fee2e2', color: '#ef4444', borderColor: '#fca5a5' }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination" role="navigation" aria-label="Pagination controls">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary"
              aria-label="Previous page"
              style={{ fontFamily: 'inherit', fontSize: '14px' }}
            >
              Previous ⬅️
            </button>
            <span aria-live="polite">Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="btn btn-secondary"
              aria-label="Next page"
              style={{ fontFamily: 'inherit', fontSize: '14px' }}
            >
              Next ➡️
            </button>
          </div>
        )}
      </div>

      {/* CREATE NEW REQUEST FORM MODAL */}
      {showForm && (
        <div className="form-overlay" onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
          <div className="form-modal" role="dialog" aria-labelledby="form-title" aria-modal="true" style={{ padding: '2rem' }}>
            <button
              className="close-btn"
              onClick={() => setShowForm(false)}
              aria-label="Close form"
              style={{ fontFamily: 'inherit' }}
            >
              <X size={20} />
            </button>
            <h2 id="form-title" style={{ fontSize: '1.4rem', fontWeight: '800', color: theme?.palette?.primary?.main || '#1e293b', marginBottom: '1.5rem', padding: '0 0.5rem' }}>
              <FileText size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} /> Create New Request
            </h2>
            <form onSubmit={handleSubmit} className="request-form" aria-label="Create new request form">
              <div className="uf-form-section">
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', marginBottom: '1rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <User size={18} /> Request Information
                </h3>
                <div className="uf-form-grid">
                  <div className="uf-form-group" style={{ position: 'relative' }} ref={dropdownRef}>
                    <label className="uf-form-label" htmlFor="employeeSearch">
                      Employee <span className="required">*</span> {errors.employeeId && <AlertCircle size={14} style={{ color: '#ef4444' }} />}
                    </label>

                    {/* --- CUSTOM SEARCHABLE SELECT --- */}
                    <div className="searchable-select-wrapper">
                      <div className="input-with-icon">
                        <input
                          id="employeeSearch"
                          type="text"
                          placeholder="Search Employee..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setFormData({ ...formData, employeeId: '' }); // Clear ID on type to force re-selection
                            setShowDropdown(true);
                          }}
                          onFocus={() => setShowDropdown(true)}
                          className={`uf-form-input ${errors.employeeId ? 'error' : ''}`}
                          autoComplete="off"
                          style={{ paddingRight: '30px', width: '100%' }}
                        />
                        <Search size={16} className="search-icon-inside" />
                      </div>

                      {showDropdown && (
                        <div className="custom-dropdown-list">
                          {filteredEmployees.length > 0 ? (
                            filteredEmployees.map(emp => (
                              <div
                                key={emp.employeeId}
                                className="dropdown-item"
                                onClick={() => handleEmployeeSelect(emp)}
                              >
                                {emp.firstName} {emp.lastName}
                              </div>
                            ))
                          ) : (
                            <div className="dropdown-item no-results">No employees found</div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* -------------------------------- */}

                    {errors.employeeId && <div className="uf-form-error-msg">{errors.employeeId}</div>}
                  </div>
                  <div className="uf-form-group">
                    <label className="uf-form-label" htmlFor="type">Request Type <span className="required">*</span></label>
                    <select
                      id="type"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className={`uf-form-select ${errors.type ? 'error' : ''}`}
                      aria-describedby="type-error"
                    >
                      <option value="Document Request">📄 Document Request</option>
                      <option value="Equipment Request">💻 Equipment Request</option>
                      <option value="Benefits Inquiry">🏥 Benefits Inquiry</option>
                      <option value="Payroll Question">💰 Payroll Question</option>
                      <option value="Training Request">📚 Training Request</option>
                      <option value="Other">❓ Other</option>
                    </select>
                  </div>
                </div>

                <div className="uf-form-group full-width" style={{ marginTop: '1rem' }}>
                  <label className="uf-form-label" htmlFor="priority">Priority Level</label>
                  <select
                    className="uf-form-select"
                    id="priority"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <option value="Low">🟢 Low</option>
                    <option value="Medium">🟡 Medium</option>
                    <option value="High">🔴 High</option>
                  </select>
                </div>

                <div className="uf-form-group full-width" style={{ marginTop: '1rem' }}>
                  <label className="uf-form-label" htmlFor="description">
                    Description <span className="required">*</span> {errors.description && <AlertCircle size={14} style={{ color: '#ef4444' }} />}
                  </label>
                  <RichTextEditor
                    value={formData.description || ''}
                    onChange={(val) => setFormData({ ...formData, description: val })}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {stripHtml(formData.description).length}/1000 characters
                    </span>
                    {errors.description && <span className="uf-form-error-msg">{errors.description}</span>}
                  </div>
                </div>
              </div>
              <div className="uf-form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <><span className="axo-btn-spinner" />Submitting...</>
                  ) : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- VIEW DETAILS MODAL --- */}
      {selectedRequest && (
        <div className="form-overlay" onClick={(e) => e.target === e.currentTarget && setSelectedRequest(null)}>
          <div className="form-modal detail-modal" style={{ padding: '2rem', maxWidth: '700px' }}>
            <button className="close-btn" onClick={() => setSelectedRequest(null)} aria-label="Close details">
              <X size={20} />
            </button>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: `1px solid ${theme?.palette?.mode === 'dark' ? '#334155' : '#e2e8f0'}`, paddingBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.4rem', fontWeight: '800', color: theme?.palette?.primary?.main || '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={20} />
                Details: {selectedRequest.type}
              </h3>
              <div className="status-cell">
                {getStatusIcon(selectedRequest.status)}
                <span className={`status-badge ${selectedRequest.status?.toLowerCase()}`}>
                  {selectedRequest.status?.toUpperCase()}
                </span>
              </div>
            </div>

            <div className="detail-content">
              <div className="uf-form-grid">
                <div className="uf-form-group">
                  <label className="uf-form-label" style={{ color: '#64748b' }}>Employee</label>
                  <div style={{ fontSize: '1rem', fontWeight: '500', color: theme?.palette?.mode === 'dark' ? '#f8fafc' : '#1e293b' }}>{selectedRequest.employeeName}</div>
                </div>
                <div className="uf-form-group">
                  <label className="uf-form-label" style={{ color: '#64748b' }}>Submitted Date</label>
                  <div style={{ fontSize: '1rem', fontWeight: '500', color: theme?.palette?.mode === 'dark' ? '#f8fafc' : '#1e293b' }}>{formatDate(selectedRequest.createdDate)}</div>
                </div>
                <div className="uf-form-group">
                  <label className="uf-form-label" style={{ color: '#64748b' }}>Priority</label>
                  <div className="detail-value">
                    <span
                      className={`priority-badge ${selectedRequest.priority?.toLowerCase()}`}
                      style={{ backgroundColor: getPriorityColor(selectedRequest.priority) }}
                    >
                      {selectedRequest.priority}
                    </span>
                  </div>
                </div>
                <div className="uf-form-group">
                  <label className="uf-form-label" style={{ color: '#64748b' }}>Created By</label>
                  <div style={{ fontSize: '1rem', fontWeight: '500', color: theme?.palette?.mode === 'dark' ? '#f8fafc' : '#1e293b' }}>{selectedRequest.createdBy}</div>
                </div>

                <div className="uf-form-group full-width">
                  <label className="uf-form-label" style={{ color: '#64748b' }}>Description</label>
                  <div
                    style={{ padding: '1rem', backgroundColor: theme?.palette?.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc', borderRadius: '8px', border: `1px solid ${theme?.palette?.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, fontSize: '0.95rem', color: theme?.palette?.mode === 'dark' ? '#cbd5e1' : '#334155', lineHeight: '1.6' }}
                    dangerouslySetInnerHTML={{ __html: selectedRequest.description || '' }}
                  />
                </div>

                <div className="uf-form-group">
                  <label className="uf-form-label" style={{ color: '#64748b' }}>Last Modified By</label>
                  <div style={{ fontSize: '1rem', fontWeight: '500', color: theme?.palette?.mode === 'dark' ? '#f8fafc' : '#1e293b' }}>{selectedRequest.lastModifiedBy}</div>
                </div>
                <div className="uf-form-group">
                  <label className="uf-form-label" style={{ color: '#64748b' }}>Last Modified Date</label>
                  <div style={{ fontSize: '1rem', fontWeight: '500', color: theme?.palette?.mode === 'dark' ? '#f8fafc' : '#1e293b' }}>{formatAuditDate(selectedRequest.lastModifiedDate)}</div>
                </div>
              </div>
            </div>

            <div className="uf-form-actions" style={{ marginTop: '2rem' }}>
              {selectedRequest.status?.toLowerCase() === 'pending' ? (
                <>
                  <button
                    onClick={() => handleReject(selectedRequest.requestId)}
                    className="btn btn-secondary"
                    disabled={loading}
                    style={{ backgroundColor: '#fee2e2', color: '#ef4444', borderColor: '#fca5a5' }}
                  >
                    ❌ Reject
                  </button>
                  <button
                    onClick={() => handleApprove(selectedRequest.requestId)}
                    className="btn btn-primary"
                    disabled={loading}
                    style={{ backgroundColor: '#10b981', color: '#ffffff', borderColor: '#059669' }}
                  >
                    ✅ Approve
                  </button>
                  {/* <button
                    onClick={() => handleDelete(selectedRequest.requestId)}
                    className="btn btn-secondary"
                    disabled={loading}
                    style={{ backgroundColor: '#fee2e2', color: '#ef4444', borderColor: '#fca5a5' }}
                  >
                    🗑️ Delete
                  </button> */}
                </>
              ) : (
                <div style={{ flex: 1 }}></div>
              )}
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedRequest(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles reused and adapted from the previous component + NEW STYLES FOR SEARCHABLE SELECT */}
      <style jsx>{`
        :root {
          --primary-color: #3b82f6;
          --gray-light: #f8fafc;
          --text-primary: #111827;
          --text-secondary: #4b5563;
        }
        .request-content.blurred {
          pointer-events: none;
        }

        /* --- NEW STYLES FOR SEARCHABLE SELECT --- */
        .searchable-select-wrapper {
            position: relative;
            width: 100%;
        }
        .input-with-icon {
            position: relative;
            display: flex;
            align-items: center;
        }
        .search-icon-inside {
            position: absolute;
            right: 10px;
            top: 50%; /* Center vertically based on container, but need to account for margin */
            transform: translateY(20%); /* Adjusted manually to fit the input height with margin */
            color: #9ca3af;
            pointer-events: none;
        }
        .custom-dropdown-list {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            max-height: 200px;
            overflow-y: auto;
            z-index: 1000;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .dropdown-item {
            padding: 10px 12px;
            cursor: pointer;
            border-bottom: 1px solid #f3f4f6;
            color: #374151;
            font-size: 14px;
        }
        .dropdown-item:last-child {
            border-bottom: none;
        }
        .dropdown-item:hover {
            background-color: #f3f4f6;
        }
        .dropdown-item.no-results {
            color: #9ca3af;
            text-align: center;
            cursor: default;
        }
        /* ---------------------------------------- */

        /* Detail Modal Specific Styles */
        .detail-modal {
           max-width: 700px;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }
        .form-section-title {
           display: flex;
           align-items: center;
           gap: 0.5rem;
           margin: 0;
           font-size: 1.25rem;
           font-weight: 600;
           color: #111827;
        }
        .detail-grid {
           display: grid;
           grid-template-columns: 1fr 1fr;
           gap: 1.5rem;
           padding: 0.5rem;
        }
        .detail-group {
           display: flex;
           flex-direction: column;
           gap: 0.25rem;
        }
        .detail-group.full-width {
           grid-column: 1 / -1;
        }
        .detail-label {
           font-size: 0.75rem;
           text-transform: uppercase;
           letter-spacing: 0.05em;
           color: #4b5563;
           font-weight: 600;
        }
        .detail-value {
           font-size: 1rem;
           color: #111827;
        }
        .box-value {
           background: #f8fafc;
           padding: 1rem;
           border-radius: 8px;
           border: 1px solid #e2e8f0;
           min-height: 80px;
           white-space: pre-wrap;
        }
        .status-badge {
          padding: 0.375rem 0.75rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          white-space: nowrap;
        }
        .status-badge.pending { background: #fef9c3; color: #d97706; }
        .status-badge.approved { background: #dcfce7; color: #16a34a; }
        .status-badge.rejected { background: #fee2e2; color: #991b1b; }
        .status-badge.cancelled { background: #f3f4f6; color: #6b7280; }
        
        .status-icon.approved { color: #10b981; }
        .status-icon.rejected { color: #ef4444; }
        .status-icon.pending { color: #f59e0b; }
        .status-icon.cancelled { color: #6b7280; }
        
        .btn-success {
           background-color: #28a745;
           color: white;
           border: none;
           padding: 8px 16px;
           border-radius: 4px;
           cursor: pointer;
        }
        .btn-success:hover { background-color: #218838; }
        
        .btn-danger {
           background-color: #dc3545;
           color: white;
           border: none;
           padding: 8px 16px;
           border-radius: 4px;
           cursor: pointer;
        }
        .btn-danger:hover { background-color: #c82333; }
      `}</style>
    </div>
  );
};

export default EmployeeRequests;
